package com.example.mystybag.appCore.logic.pages

import com.example.mystybag.appCore.logic.BagPageCallback
import com.example.mystybag.appCore.logic.NavigatorSelectPage
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_DELIVERY
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_FAVORITES
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_HOME
import com.example.mystybag.appCore.logic.MystyBagDigitalWalletViewModel.Companion.PAGE_PROFILE
import com.example.mystybag.common.resources.SR
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.logic.base.AppContext
import com.example.mystybag.core.logic.base.ChildViewModel
import com.example.mystybag.core.logic.base.MainCallback
import com.example.mystybag.appCore.model.pages.BagUIState
import com.example.mystybag.sharedmodels.project.responseModels.HomeTopPickUiModel
import com.example.mystybag.communication.common.CommonCommands.addFavoriteCommand
import com.example.mystybag.communication.common.CommonCommands.createReservationCommand
import com.example.mystybag.communication.common.CommonCommands.getMyPosTransactionStatus
import com.example.mystybag.communication.common.CommonCommands.getBagDetails
import com.example.mystybag.communication.common.CommonCommands.getLocationProducts
import com.example.mystybag.communication.common.CommonCommands.getMerchantLocations
import com.example.mystybag.communication.common.CommonCommands.removeFavoriteCommand
import com.example.mystybag.communication.utils.onResponse
import com.example.mystybag.core.defines.UtilsPreferencesDefines.PREFERENCES_USER_ID
import com.example.mystybag.core.expected.getCurrentUserLocation
import com.example.mystybag.core.expected.openInExternalBrowser
import com.example.mystybag.core.expected.Preferences
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.openLargeMap
import com.example.mystybag.core.logic.base.AppViewModel
import com.example.mystybag.permissionsUtils.expected.Permission
import com.example.mystybag.permissionsUtils.expected.PermissionHelper
import com.example.mystybag.sharedmodels.project.responseModels.calculateDistanceMeters
import com.example.mystybag.core.model.FullScreenImageOverlay
import com.example.mystybag.core.security.Security
import com.example.mystybag.url.logic.NavigatorURLView
import com.example.mystybag.sharedmodels.project.requestModels.favorites.AddFavoriteRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.favorites.RemoveFavoriteRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetLocationProductsRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.merchant.GetMerchantLocationsRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.reservations.CreateReservationRequestModel
import com.example.mystybag.sharedmodels.project.requestModels.reservations.GetMyPosTransactionStatusRequestModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetBagDetailsResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetLocationProductsResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.ProductModel
import com.example.mystybag.sharedmodels.project.responseModels.merchant.GetMerchantLocationsResponseModel
import com.example.mystybag.sharedmodels.project.responseModels.reservations.CreateReservationResponseModel
import io.ktor.util.encodeBase64
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.coroutines.delay
import kotlinx.coroutines.withContext
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

class BagViewModel(
    context: AppContext,
    private val bagItem: HomeTopPickUiModel?
) : ChildViewModel<BagUIState>(context, BagUIState()) {

    private enum class PaymentParametersRequired(val apiValue: String) {
        FULL("1"),
        SIMPLIFIED("2"),
        ULTRA_SIMPLIFIED("3")
    }

    companion object {
        private const val PAYMENT_POLL_INTERVAL_MS = 3000L
        private const val PAYMENT_POLL_MAX_ATTEMPTS = 20
        private const val FORCE_MYPOS_SANDBOX = true
        private const val USE_MYPOS_WRAPPER = true
        private const val MYPOS_WRAPPER_URL = "https://mypos.checkout.mystybag.com/"
        private const val OPEN_WRAPPER_IN_EXTERNAL_BROWSER = false
        private const val WRAPPER_DEBUG = false
        private const val WRAPPER_USE_MYPOS_STATUS_SCREEN = true
        private const val WRAPPER_SUCCESS_DELAY_SECONDS = 10
        private const val WRAPPER_SHOW_HEADER = false
        private const val WRAPPER_SHOW_BACK_BUTTON = false
        private const val WRAPPER_HEADER_TITLE = "MystyBag"
        private val PAYMENT_PARAMETERS_REQUIRED = PaymentParametersRequired.ULTRA_SIMPLIFIED
    }

    private var activeCheckoutTransactionReference: String? = null
    private var activeCheckoutOpenedExternally: Boolean = false
    private var lastPaidReservationId: Long? = null
    private var paymentSuccessDispatched: Boolean = false
    
    override fun onCreate() {
        navCallbacks.controls.value.backButtonVisible = true

        if(bagItem != null){
            // Initialize state from HomeTopPickUiModel
            val baseRating = bagItem.rating
            updateState { state ->
                state.copy(
                    bagId = bagItem.id.toLongOrNull() ?: 0L,
                    merchantId = bagItem.merchantId,
                    merchantLocationId = bagItem.merchantLocationId,
                    merchantTitle = bagItem.title,
                    merchantImageUrl = bagItem.merchantImageUrl,
                    locationCoverUrl    = bagItem.locationCoverUrl,
                    merchantAllergens = bagItem.merchantAllergies,
                    allergens = bagItem.allergens ?: emptyList(),
                    locationTitle = bagItem.locationTitle ?: "",
                    locationAddress = bagItem.locationAddress ?: "",
                    distance = bagItem.distance,
                    cityName = bagItem.cityName,
                    locationLatitude = bagItem.locationLatitude,
                    locationLongitude = bagItem.locationLongitude,
                    bagTitle = bagItem.bagTitle,
                    isSellingFast = bagItem.isSellingFast,
                    isFavorite = bagItem.isFavorite,
                    containsProductsForPersonsOver18 = bagItem.containsProductsForPersonsOver18,
                    averageRating = baseRating,
                    reviewCount = bagItem.reviewCount ?: 0,
                    // Use detailed ratings from API if available, otherwise calculate from average
                    ratingCategory1 = bagItem.ratingCategory1
                        ?: (baseRating + 0.3).coerceIn(0.0, 5.0),
                    ratingCategory2 = bagItem.ratingCategory2
                        ?: (baseRating + 0.1).coerceIn(0.0, 5.0),
                    ratingCategory3 = bagItem.ratingCategory3
                        ?: (baseRating - 0.2).coerceIn(0.0, 5.0),
                    ratingCategory4 = bagItem.ratingCategory4
                        ?: (baseRating - 0.3).coerceIn(0.0, 5.0),
                    collectWindowLabel = bagItem.collectInfo,
                    priceNormal = parsePrice(bagItem.oldPrice),
                    priceWithDiscount = parsePrice(bagItem.newPrice),
                    currency = extractCurrency(bagItem.newPrice),
                    bagsCount = bagItem.bagsCount,
                    bagsReservationCount = bagItem.bagsReservationCount,
                    bagsAvailable = bagItem.bagsAvailable,
                    businessCategoryId = bagItem.businessCategoryId,
                    businessCategoryTitle = bagItem.businessCategoryTitle,
                    businessCategoryDescription = bagItem.businessCategoryDescription,
                    products = bagItem.products,
                    isLoading = false
                )
            }
            // Fetch fresh availability from server so "X още налични" matches DB (list data can be stale)
            loadBagAvailability()
            // Load location menu photos (used in bag details bottom section).
            loadLocationMenuPhotos()
            // Load the location's catalog categories so the possible-products view can
            // group items by category when the bag spans more than one group.
            loadLocationProductCategories()
        }
    }

    /**
     * The location's full catalog products (each carrying its [ProductModel.categoryId]).
     * The bag's own products often arrive without a category, so we use this catalog to
     * back-fill [ProductModel.categoryId] (see [withResolvedCategories]) so the
     * possible-products view can group them.
     */
    private var locationCatalogProducts: List<ProductModel> = emptyList()

    /** Loads the location's catalog (categories + products) used to group product lists by category. */
    private fun loadLocationProductCategories() {
        val locationId = uiState.value.merchantLocationId ?: return
        if (locationId <= 0L) return
        scope.launch {
            commandBlock(showProgress = false) {
                getLocationProducts(GetLocationProductsRequestModel(merchantLocationId = locationId)).onResponse(
                    success = { response ->
                        val catalog = response as? GetLocationProductsResponseModel ?: return@onResponse
                        locationCatalogProducts = catalog.products
                        launchOnMain {
                            updateState {
                                it.copy(
                                    productCategories = catalog.categories,
                                    // Back-fill categories onto whatever products are already loaded,
                                    // so a list that arrived before the catalog still gets grouped.
                                    products = it.products.withResolvedCategories()
                                )
                            }
                        }
                    },
                    baseError = {
                        // Keep current state on transient API errors; view falls back to a flat list.
                    },
                    error = {
                        // Keep current state on transient API errors; view falls back to a flat list.
                    }
                )
            }
        }
    }

    /**
     * Fills in [ProductModel.categoryId] for products that don't have one by matching them
     * against [locationCatalogProducts] — first by product id, then by (case-insensitive,
     * trimmed) name. Products that already have a category, or that can't be matched, are
     * returned unchanged. This is what lets the possible-products view show items grouped by
     * category instead of one flat, mixed list.
     */
    private fun List<ProductModel>.withResolvedCategories(): List<ProductModel> {
        val catalog = locationCatalogProducts
        if (catalog.isEmpty() || isEmpty()) return this
        val categoryById = catalog.associate { it.id to it.categoryId }
        val categoryByName = catalog
            .filter { it.name.isNotBlank() && it.categoryId != null }
            .associate { it.name.trim().lowercase() to it.categoryId }
        return map { product ->
            if (product.categoryId != null) {
                product
            } else {
                val resolved = categoryById[product.id]
                    ?: categoryByName[product.name.trim().lowercase()]
                if (resolved != null) product.copy(categoryId = resolved) else product
            }
        }
    }

    /** Fetches current bag availability from API and updates state (fixes mismatch with DB). */
    private fun loadBagAvailability() {
        val bagId = uiState.value.bagId
        if (bagId == 0L) {
            return
        }
        scope.launch {
            commandBlock(showProgress = false) {
                getBagDetails(bagId).onResponse(
                    success = { response ->
                        val details = response as? GetBagDetailsResponseModel ?: run {
                            return@onResponse
                        }
                        val available = details.availableCount
                            ?: ((details.totalCount ?: 0) - (details.reservedCount ?: 0)).coerceAtLeast(0)
                        launchOnMain {
                            updateState {
                                it.copy(
                                    bagsAvailable = available,
                                    bagsCount = details.totalCount ?: it.bagsCount,
                                    bagsReservationCount = details.reservedCount ?: it.bagsReservationCount,
                                    products = (details.products.takeIf { p -> p.isNotEmpty() } ?: it.products)
                                        .withResolvedCategories(),
                                    // Allergens are derived server-side from the bag's products; refresh
                                    // them here so the allergens view reflects the current configuration.
                                    allergens = details.allergens ?: it.allergens,
                                    containsProductsForPersonsOver18 = details.containsProductsForPersonsOver18
                                )
                            }
                        }
                    },
                    baseError = {
                        // Keep current list data on transient API errors.
                    }
                )
            }
        }
    }

    private fun loadLocationMenuPhotos() {
        val merchantId = uiState.value.merchantId
        if (merchantId == 0L) return

        scope.launch {
            commandBlock(showProgress = false) {
                getMerchantLocations(GetMerchantLocationsRequestModel(merchantId = merchantId)).onResponse(
                    success = { response ->
                        val locations = (response as? GetMerchantLocationsResponseModel)?.locations ?: emptyList()
                        val selectedLocation = uiState.value.merchantLocationId?.let { selectedId ->
                            locations.find { it.id == selectedId }
                        } ?: locations.firstOrNull()
                        val cleanedUrls = (selectedLocation?.menuPhotoUrls ?: emptyList())
                            .map { it.trim() }
                            .filter { it.isNotEmpty() }
                            .distinct()
                        val fallbackProducts = selectedLocation?.products ?: emptyList()
                        launchOnMain {
                            updateState { state ->
                                state.copy(
                                    menuPhotoUrls = cleanedUrls,
                                    products = (state.products.takeIf { it.isNotEmpty() }
                                        ?: fallbackProducts).withResolvedCategories()
                                )
                            }
                        }
                    },
                    baseError = {
                        // Keep existing state on transient API errors.
                    },
                    error = {
                        // Keep existing state on transient API errors.
                    }
                )
            }
        }
    }
    
    private fun parsePrice(priceString: String): Double {
        // Parse price string like "12,00 €" to 12.0
        return priceString
            .replace(",", ".")
            .replace(Regex("[^0-9.]"), "")
            .toDoubleOrNull() ?: 0.0
    }
    
    private fun extractCurrency(priceString: String): String {
        // Extract currency from price string like "12,00 €"
        val currencyMatch = Regex("[A-Z]{3}|[€$£¥]").find(priceString)
        return currencyMatch?.value ?: "€"
    }
    
    fun onBackClick() {
        goBack()
    }
    
    fun onShareClick() {
        // TODO: Implement share functionality
    }
    
    fun onFavoriteClick() {
        if (uiState.value.isFavorite) {
            removeFavorite()
        } else {
            addFavorite()
        }
    }
    
    private fun addFavorite() {
        val merchantLocationId = uiState.value.merchantLocationId
        if (merchantLocationId == null) {
            // TODO: Show error - merchantLocationId is required
            return
        }
        commandBlock(showProgress = false, hideProgress = false) {
            addFavoriteCommand(
                AddFavoriteRequestModel(
                    userId = Preferences.getLong(PREFERENCES_USER_ID, 0L),
                    merchantLocationId = merchantLocationId
                )
            ).onResponse(
                success = {
                    updateState { it.copy(isFavorite = true) }
                    // Favorites page will refresh automatically when user navigates back (onResume)
                },
                error = {
                    // TODO: error UI
                }
            )
        }
    }
    
    private fun removeFavorite() {
        val merchantLocationId = uiState.value.merchantLocationId
        if (merchantLocationId == null) {
            // TODO: Show error - merchantLocationId is required
            return
        }
        commandBlock(showProgress = false, hideProgress = false) {
            removeFavoriteCommand(
                RemoveFavoriteRequestModel(
                    userId = Preferences.getLong(PREFERENCES_USER_ID, 0L),
                    merchantLocationId = merchantLocationId
                )
            ).onResponse(
                success = {
                    updateState { it.copy(isFavorite = false) }
                    // Favorites page will refresh automatically when user navigates back (onResume)
                },
                error = {
                    // TODO: error UI
                }
            )
        }
    }
    
    
    fun onReserveClick() {
        if (uiState.value.bagsAvailable <= 0) {
            return
        }
        // If the pickup window is currently open, warn the user first that they need to
        // pick up the bag immediately; otherwise proceed directly to the reserve dialog.
        if (isPickupWindowOpenNow(uiState.value.collectWindowLabel)) {
            openPickupNowConfirm()
        } else {
            openReserveDialog()
        }
    }

    private fun openReserveDialog() {
        updateState {
            it.copy(
                showReserveDialog = true,
                reserveQuantity = 1.coerceIn(1, it.bagsAvailable),
                reserveDialogLoading = false,
                checkoutInProgress = false,
                reserveDialogErrorMessage = ""
            )
        }
    }

    private fun openPickupNowConfirm() {
        // Show confirmation immediately; travel times are computed asynchronously and
        // filled in once we have the user's location (if permission was already granted).
        updateState {
            it.copy(
                showPickupNowConfirm = true,
                pickupNowWalkMinutes = null,
                pickupNowDriveMinutes = null,
                pickupNowHasLocationPermission = false
            )
        }
        scope.launch {
            val hasPermission = PermissionHelper.hasPermission(Permission.Location)
            if (!hasPermission) {
                launchOnMain {
                    updateState { it.copy(pickupNowHasLocationPermission = false) }
                }
                return@launch
            }
            val userLocation = getCurrentUserLocation()
            val locLat = uiState.value.locationLatitude
            val locLon = uiState.value.locationLongitude
            if (userLocation == null || (locLat == 0.0 && locLon == 0.0)) {
                launchOnMain {
                    updateState { it.copy(pickupNowHasLocationPermission = true) }
                }
                return@launch
            }
            val (userLat, userLon) = userLocation
            val meters = calculateDistanceMeters(userLat, userLon, locLat, locLon)
            // Rough estimates without an external routing API: 5 km/h walking, 30 km/h urban driving.
            val walkMinutes = ((meters / 1000.0) * 60.0 / 5.0).coerceAtLeast(1.0).toInt()
            val driveMinutes = ((meters / 1000.0) * 60.0 / 30.0).coerceAtLeast(1.0).toInt()
            launchOnMain {
                updateState {
                    it.copy(
                        pickupNowHasLocationPermission = true,
                        pickupNowWalkMinutes = walkMinutes,
                        pickupNowDriveMinutes = driveMinutes
                    )
                }
            }
        }
    }

    fun onPickupNowConfirmCancel() {
        updateState {
            it.copy(
                showPickupNowConfirm = false,
                pickupNowWalkMinutes = null,
                pickupNowDriveMinutes = null
            )
        }
    }

    fun onPickupNowConfirmContinue() {
        updateState {
            it.copy(
                showPickupNowConfirm = false,
                pickupNowWalkMinutes = null,
                pickupNowDriveMinutes = null
            )
        }
        openReserveDialog()
    }

    /**
     * Returns true when [label] describes a "today" pickup window (e.g. "Collect today 19:30 - 20:00"
     * or "Вземи днес 19:30 - 20:00") and the current local time falls inside the window.
     */
    private fun isPickupWindowOpenNow(label: String): Boolean {
        if (label.isBlank()) return false
        val todayPrefix = sharedString(SR.strings.collect_today).trim()
        if (todayPrefix.isBlank() || !label.contains(todayPrefix, ignoreCase = true)) return false
        val range = parseCollectTimeRange(label) ?: return false
        val now = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault())
        val nowMinutes = now.hour * 60 + now.minute
        return nowMinutes in range.first..range.second
    }

    /**
     * Extracts the first `HH:MM - HH:MM` (or `HH.MM`) range from [label] and returns it as
     * (fromMinutes, toMinutes) since midnight, or null if no valid range is found.
     */
    private fun parseCollectTimeRange(label: String): Pair<Int, Int>? {
        val regex = Regex("""(\d{1,2})[:.](\d{2})\s*[-–—]\s*(\d{1,2})[:.](\d{2})""")
        val match = regex.find(label) ?: return null
        val (fromH, fromM, toH, toM) = match.destructured
        val from = fromH.toInt() * 60 + fromM.toInt()
        val to = toH.toInt() * 60 + toM.toInt()
        if (from !in 0..(24 * 60) || to !in 0..(24 * 60) || to < from) return null
        return from to to
    }

    fun onReserveDialogClose() {
        if (uiState.value.reserveDialogLoading || uiState.value.checkoutInProgress) {
            return
        }
        updateState { it.copy(showReserveDialog = false, reserveDialogErrorMessage = "") }
    }

    fun onDecreaseReserveQuantity() {
        val current = uiState.value.reserveQuantity
        if (current <= 1) return
        updateState { it.copy(reserveQuantity = current - 1, reserveDialogErrorMessage = "") }
    }

    fun onIncreaseReserveQuantity() {
        val max = uiState.value.bagsAvailable.coerceAtLeast(0)
        val current = uiState.value.reserveQuantity
        if (current >= max) return
        updateState { it.copy(reserveQuantity = current + 1, reserveDialogErrorMessage = "") }
    }

    fun confirmReservation() {
        if (uiState.value.reserveDialogLoading || uiState.value.checkoutInProgress) return
        val available = uiState.value.bagsAvailable
        if (available <= 0) return
        val qty = uiState.value.reserveQuantity.coerceIn(1, available)
        updateState { it.copy(reserveDialogLoading = true, reserveDialogErrorMessage = "") }

        commandBlock(showProgress = false, hideProgress = false) {
            createReservationCommand(
                CreateReservationRequestModel(
                    userId = Preferences.getLong(PREFERENCES_USER_ID, 0L),
                    bagId = uiState.value.bagId,
                    bagsCount = qty
                )
            ).onResponse(
                success = { response: CreateReservationResponseModel ->
                    startCheckoutFlow(
                        reservationResponse = response
                    )
                },
                error = {
                    updateState {
                        it.copy(
                            reserveDialogLoading = false,
                            checkoutInProgress = false,
                            reserveDialogErrorMessage = sharedString(SR.strings.oops_something_went_wrong)
                        )
                    }
                }
            )
        }
    }

    private suspend fun startCheckoutFlow(reservationResponse: CreateReservationResponseModel) {
        val transactionReference = reservationResponse.transactionReference
        val backendPaymentUrl = reservationResponse.paymentUrl.trim()
        val backendCheckoutUrl = reservationResponse.checkoutUrl.trim()
        val backendPreferredUrl = backendPaymentUrl.ifBlank { backendCheckoutUrl }
        val checkoutRequest = buildSandboxMyPosCheckoutPostRequest(reservationResponse)
        val generatedCheckoutUrl = checkoutRequest.first
        val checkoutPostBody = checkoutRequest.second
        val hasEmptySignature = checkoutPostBody.contains("Signature=") &&
            Regex("""(^|&)Signature=(&|$)""").containsMatchIn(checkoutPostBody)
        val shouldUseBackendCheckoutOnWeb = PlatformHelper.isWeb && hasEmptySignature
        val wrapperCheckoutUrl = if (USE_MYPOS_WRAPPER && checkoutPostBody.isNotBlank() && !shouldUseBackendCheckoutOnWeb) {
            val debugQuery = if (WRAPPER_DEBUG) "&debug=1" else ""
            val wrapperUiQuery = linkedMapOf(
                "useMyPosStatusScreen" to if (WRAPPER_USE_MYPOS_STATUS_SCREEN) "1" else "0",
                "successRedirectDelaySec" to WRAPPER_SUCCESS_DELAY_SECONDS.toString(),
                "autoReturnToApp" to "1",
                "showAppHeader" to if (WRAPPER_SHOW_HEADER) "1" else "0",
                "showBackButton" to if (WRAPPER_SHOW_BACK_BUTTON) "1" else "0",
                "headerTitle" to WRAPPER_HEADER_TITLE,
                "onBack" to "returnToApp",
                "targetTab" to "reservations"
            ).entries.joinToString("&") { (key, value) ->
                "${urlEncodeFormComponent(key)}=${urlEncodeFormComponent(value)}"
            }
            "$MYPOS_WRAPPER_URL?isSandbox=true$debugQuery&$wrapperUiQuery&$checkoutPostBody"
        } else {
            ""
        }
        val sandboxCredentials =
            reservationResponse.sid == "000000000000010" &&
                reservationResponse.walletNumber == "61938166610"
        val checkoutUrl = when {
            shouldUseBackendCheckoutOnWeb && backendPreferredUrl.isNotBlank() -> backendPreferredUrl
            wrapperCheckoutUrl.isNotBlank() -> wrapperCheckoutUrl
            FORCE_MYPOS_SANDBOX -> generatedCheckoutUrl
            backendCheckoutUrl.isBlank() -> generatedCheckoutUrl
            sandboxCredentials && backendCheckoutUrl.contains("/vmp/checkout") && !backendCheckoutUrl.contains("/vmp/checkout-test") ->
                backendCheckoutUrl.replace("/vmp/checkout", "/vmp/checkout-test")
            else -> backendCheckoutUrl
        }
        val checkoutPostBodyForNavigation = when {
            shouldUseBackendCheckoutOnWeb -> ""
            wrapperCheckoutUrl.isNotBlank() -> ""
            else -> checkoutPostBody
        }
        if (transactionReference.isBlank() || checkoutUrl.isBlank()) {
            updateState {
                it.copy(
                    reserveDialogLoading = false,
                    checkoutInProgress = false,
                    reserveDialogErrorMessage = "Плащането не е налично в момента. Опитайте отново след малко."
                )
            }
            return
        }

        updateState {
            it.copy(
                reserveDialogLoading = false,
                checkoutInProgress = true,
                reserveDialogErrorMessage = ""
            )
        }
        lastPaidReservationId = null
        paymentSuccessDispatched = false
        activeCheckoutTransactionReference = transactionReference
        if (wrapperCheckoutUrl.isNotBlank() && OPEN_WRAPPER_IN_EXTERNAL_BROWSER) {
            activeCheckoutOpenedExternally = true
            launchOnMain {
                openInExternalBrowser(wrapperCheckoutUrl)
            }
        } else {
            activeCheckoutOpenedExternally = false
            // Must complete navigation before polling: a fast "paid" response (common for card flows)
            // used to race with launchOnMain { navigate(...) }, so URLView was not on the stack yet and
            // onEmbeddedCheckoutServerConfirmedPaid() could not call onCheckoutSuccessSignal() — no footer CTA.
            withContext(Dispatchers.Main) {
                navigate(
                    NavigatorURLView(
                        url = checkoutUrl,
                        title = sharedString(SR.strings.payment_methods),
                        postBody = checkoutPostBodyForNavigation,
                        successUrlPrefix = reservationResponse.returnUrl,
                        cancelUrlPrefix = reservationResponse.cancelUrl,
                        transactionReference = transactionReference,
                        reservationId = reservationResponse.reservationId
                    )
                )
            }
        }
        pollMyPosTransactionResult(transactionReference, reservationResponse.reservationId)
    }

    private suspend fun buildSandboxMyPosCheckoutPostRequest(reservationResponse: CreateReservationResponseModel): Pair<String, String> {
        val orderId = reservationResponse.transactionReference.toString()
        val successUrl = ensureTxParam(
            url = reservationResponse.returnUrl,
            tx = reservationResponse.transactionReference
        )
        val cancelUrl = ensureTxParam(
            url = reservationResponse.cancelUrl,
            tx = reservationResponse.transactionReference
        )
        val notifyUrl = successUrl
        val privateKey = reservationResponse.privateKey
        val params = linkedMapOf<String, String>(
            "IPCmethod" to "IPCPurchase",
            "IPCVersion" to reservationResponse.ipcVersion,
            "IPCLanguage" to reservationResponse.ipcLanguage,
            "SID" to reservationResponse.sid,
            "walletnumber" to reservationResponse.walletNumber,
            "Amount" to reservationResponse.amount,
            "Currency" to reservationResponse.currency,
            "OrderID" to orderId,
            "URL_OK" to successUrl,
            "URL_Cancel" to cancelUrl,
            "URL_Notify" to notifyUrl,
            "CardTokenRequest" to "0",
            "KeyIndex" to "1",
            "IPC_PP" to "1",
            "PaymentParametersRequired" to PAYMENT_PARAMETERS_REQUIRED.apiValue,
            "customeremail" to reservationResponse.customerEmail,
            "customerfirstnames" to reservationResponse.customerFirstName,
            "customerfamilyname" to reservationResponse.customerLastName,
            "customerphone" to reservationResponse.customerPhone,
            "customercountry" to reservationResponse.customerCountry,
            "customercity" to reservationResponse.customerCity,
            "customerzipcode" to "",
            "customeraddress" to "",
            "Note" to "",
            "CartItems" to reservationResponse.cartItems,
            "Article_1" to reservationResponse.article1,
            "Quantity_1" to reservationResponse.quantity1,
            "Price_1" to reservationResponse.price1,
            "Currency_1" to reservationResponse.currency1,
            "Amount_1" to reservationResponse.amount1,
            "Article_2" to reservationResponse.article2,
            "Quantity_2" to reservationResponse.quantity2,
            "Price_2" to reservationResponse.price2,
            "Currency_2" to reservationResponse.currency2,
            "Amount_2" to reservationResponse.amount2,

        )
        val signaturePayload = params.values.joinToString(separator = "-").encodeToByteArray().encodeBase64()
        val signature = Security.signPkcs1Sha256Base64Async(
            data = signaturePayload.encodeToByteArray(),
            privateKeyPem = privateKey
        )
        val postBody = (params + ("Signature" to signature)).entries.joinToString("&") { (key, value) ->
            "${urlEncodeFormComponent(key)}=${urlEncodeFormComponent(value)}"
        }
        return "https://www.mypos.com/vmp/checkout-test" to postBody
    }

    private fun ensureTxParam(url: String, tx: String): String {
        if (url.isBlank() || tx.isBlank()) return url
        if (url.contains("tx=")) return url
        val separator = if (url.contains("?")) "&" else "?"
        return "$url${separator}tx=$tx"
    }

    private fun urlEncodeFormComponent(value: String): String = buildString {
        value.forEach { ch ->
            when {
                ch in 'a'..'z' || ch in 'A'..'Z' || ch in '0'..'9' || ch == '-' || ch == '_' || ch == '.' || ch == '*' -> append(ch)
                ch == ' ' -> append('+')
                else -> {
                    val bytes = ch.toString().encodeToByteArray()
                    bytes.forEach { byte ->
                        val intVal = byte.toInt() and 0xFF
                        append('%')
                        append("0123456789ABCDEF"[intVal shr 4])
                        append("0123456789ABCDEF"[intVal and 0x0F])
                    }
                }
            }
        }
    }

    private suspend fun pollMyPosTransactionResult(transactionReference: String, reservationId: Long) {
        repeat(PAYMENT_POLL_MAX_ATTEMPTS) {
            if (activeCheckoutTransactionReference != transactionReference) return
            var flowFinished = false
            getMyPosTransactionStatus(
                GetMyPosTransactionStatusRequestModel(transactionReference = transactionReference)
            ).onResponse(
                success = { response ->
                    if (activeCheckoutTransactionReference != transactionReference) return@onResponse
                    val status = response.paymentStatus.lowercase()
                    val paid = status == "paid" || status == "success"
                    val failed = status == "failed" || status == "cancelled" || status == "canceled" || status == "declined"
                    if (!response.isFinal && !paid && !failed) return@onResponse

                    flowFinished = true
                    activeCheckoutTransactionReference = null
                    if (paid) {
                        lastPaidReservationId = reservationId
                        updateState {
                            it.copy(
                                showReserveDialog = false,
                                reserveDialogLoading = false,
                                checkoutInProgress = false,
                                reserveDialogErrorMessage = ""
                            )
                        }
                        // Web-only: show checkout success CTA (DOM footer in jsMain URLView); Android/iOS unchanged.
                        if (PlatformHelper.isWeb && !activeCheckoutOpenedExternally) {
                            launchOnMain {
                                setResult<BagPageCallback> { onEmbeddedCheckoutServerConfirmedPaid() }
                            }
                        }
                        // In embedded WebView flow, URLViewViewModel controls the delayed auto-return.
                        // Immediate dispatch here is only needed for external browser checkout.
                        if (activeCheckoutOpenedExternally && !paymentSuccessDispatched) {
                            paymentSuccessDispatched = true
                            setResult<BagPageCallback> { onReservationSuccess(reservationId) }
                            lastPaidReservationId = null
                        }
                    } else {
                        lastPaidReservationId = null
                        updateState {
                            it.copy(
                                reserveDialogLoading = false,
                                checkoutInProgress = false,
                                reserveDialogErrorMessage = sharedString(SR.strings.oops_something_went_wrong)
                            )
                        }
                    }
                    activeCheckoutOpenedExternally = false
                },
                baseError = {
                    // transient issue: continue polling until timeout
                },
                error = {
                    // transient issue: continue polling until timeout
                }
            )
            if (flowFinished) return
            delay(PAYMENT_POLL_INTERVAL_MS)
        }

        if (activeCheckoutTransactionReference != transactionReference) return
        activeCheckoutTransactionReference = null
        activeCheckoutOpenedExternally = false
        lastPaidReservationId = null
        updateState {
            it.copy(
                reserveDialogLoading = false,
                checkoutInProgress = false,
                reserveDialogErrorMessage = sharedString(SR.strings.oops_something_went_wrong)
            )
        }
    }

    fun resetCheckoutFlowAfterReturn() {
        val paidReservationId = lastPaidReservationId
        activeCheckoutTransactionReference = null
        activeCheckoutOpenedExternally = false
        lastPaidReservationId = null
        updateState {
            it.copy(
                showReserveDialog = false,
                reserveDialogLoading = false,
                checkoutInProgress = false,
                reserveDialogErrorMessage = ""
            )
        }
        if (paidReservationId != null && !paymentSuccessDispatched) {
            paymentSuccessDispatched = true
            setResult<BagPageCallback> { onReservationSuccess(paidReservationId) }
            return
        }
        // If payment flow was closed/cancelled for the last available bag, we should leave details
        // and return to the previous bags list, because there is nothing left to reserve.
        scope.launch {
            commandBlock(showProgress = false, hideProgress = false) {
                getBagDetails(uiState.value.bagId).onResponse(
                    success = { response ->
                        val details = response as? GetBagDetailsResponseModel ?: return@onResponse
                        val available = details.availableCount
                            ?: ((details.totalCount ?: 0) - (details.reservedCount ?: 0)).coerceAtLeast(0)
                        launchOnMain {
                            updateState {
                                it.copy(
                                    bagsAvailable = available,
                                    bagsCount = details.totalCount ?: it.bagsCount,
                                    bagsReservationCount = details.reservedCount ?: it.bagsReservationCount
                                )
                            }
                            if (available <= 0) {
                                goBack()
                            }
                        }
                    },
                    baseError = {
                        // Keep the user on details screen on transient API issues.
                    },
                    error = {
                        // Keep the user on details screen on transient API issues.
                    }
                )
            }
        }
    }

    
    fun onStoreInfoClick() {
        // TODO: Navigate to store info or show bottom sheet
    }
    
    fun onLocationClick() {
        val address = uiState.value.locationAddress
        val latitude = uiState.value.locationLatitude
        val longitude = uiState.value.locationLongitude
        
        if (address.isNotEmpty() && latitude != 0.0 && longitude != 0.0) {
            openLargeMap(latitude, longitude, address)
        }
    }

    fun onMenuPhotoClick(url: String) {
        val trimmed = url.trim().takeIf { it.isNotEmpty() } ?: return
        AppViewModel.fullScreenImageOverlay.value = FullScreenImageOverlay(trimmed) {
            AppViewModel.fullScreenImageOverlay.value = null
        }
    }
}

